// The visitor's analytics choice (privacy decision 2026-10-31). PostHog runs
// only after "Accept analytics"; before that, and after "Refuse", nothing is
// sent to it and none of its cookies or storage stay on the device.
//
// The choice itself is a strictly necessary first-party cookie on .basiqr.com,
// so basiqr.com (the landing) and the app share it: one answer covers both.
// The landing reads and writes the same cookie (landing/src/lib/consent.ts):
// keep the name, the values and the lifetime identical in both files.

export const CONSENT_COOKIE = 'basiqr_consent';
export type ConsentChoice = 'granted' | 'denied';
/** 180 days: the "6 months" the cookie notice states. */
export const CONSENT_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

const CHANGE_EVENT = 'basiqr:consent-change';
const OPEN_EVENT = 'basiqr:consent-open';

// Cookies and storage keys PostHog writes: ph_<key>_posthog (cookie, local
// and session storage), ph_<key>_window_id and the like, __ph_opt_in_out_<key>.
const ANALYTICS_KEY = /^(ph_|__ph_)/;
const EXPIRED = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';

/** The shared cookie domain on basiqr.com hosts; none elsewhere (localhost, previews). */
function sharedDomain(hostname: string): string | null {
  return hostname === 'basiqr.com' || hostname.endsWith('.basiqr.com') ? '.basiqr.com' : null;
}

/** The stored choice, or null when the visitor hasn't answered (or it expired). */
export function readConsent(cookie: string = document.cookie): ConsentChoice | null {
  for (const part of cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq > -1 && part.slice(0, eq).trim() === CONSENT_COOKIE) {
      const value = part.slice(eq + 1).trim();
      return value === 'granted' || value === 'denied' ? value : null;
    }
  }
  return null;
}

/** The Set-Cookie string for a choice on this host. */
export function consentCookieString(
  choice: ConsentChoice,
  where: { hostname: string; protocol: string } = window.location
): string {
  const domain = sharedDomain(where.hostname);
  return [
    `${CONSENT_COOKIE}=${choice}`,
    `max-age=${CONSENT_MAX_AGE_SECONDS}`,
    'path=/',
    ...(domain ? [`domain=${domain}`] : []),
    'SameSite=Lax',
    ...(where.protocol === 'https:' ? ['Secure'] : []),
  ].join('; ');
}

/** Store the choice and tell the page (config/posthog.ts starts or stops). */
export function writeConsent(choice: ConsentChoice): void {
  document.cookie = consentCookieString(choice);
  window.dispatchEvent(new CustomEvent<ConsentChoice>(CHANGE_EVENT, { detail: choice }));
}

export function onConsentChange(listener: (choice: ConsentChoice) => void): () => void {
  const handler = (event: Event) => listener((event as CustomEvent<ConsentChoice>).detail);
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** "Cookie settings": show the banner again so the visitor can change their answer. */
export function openConsentSettings(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenConsentSettings(listener: () => void): () => void {
  window.addEventListener(OPEN_EVENT, listener);
  return () => window.removeEventListener(OPEN_EVENT, listener);
}

/**
 * Remove every cookie and storage key PostHog keeps: on refusal, on
 * withdrawal, and on each load without consent (it ran without asking before
 * this change, and the landing's storage can't be reached from here).
 */
export function clearAnalyticsStorage(): void {
  const { hostname } = window.location;
  const domain = sharedDomain(hostname);
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0].trim();
    if (!ANALYTICS_KEY.test(name)) continue;
    // Wherever it may live: host-only, the bare hostname, the shared domain.
    document.cookie = `${name}=; ${EXPIRED}; path=/`;
    document.cookie = `${name}=; ${EXPIRED}; path=/; domain=${hostname}`;
    if (domain) document.cookie = `${name}=; ${EXPIRED}; path=/; domain=${domain}`;
  }
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (key && ANALYTICS_KEY.test(key)) storage.removeItem(key);
      }
    } catch {
      /* storage blocked: nothing of ours can be in it */
    }
  }
}
